import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import {
    BudgetProposalT,
    BudgetProposalWithResourcesT,
} from 'routes/BudgetModule/config/types';
import { HttpError, RQAPICall, RQAPICallWithData } from 'store/APIClient';
import { useDispatch } from 'store/hooks';
import { BudgetProposalByResourcesOptions } from './types';
import { useTranslation } from 'react-i18next';
import {
    BudgetProposalsQueryKey,
    createBudgetQueryKeys,
} from 'routes/BudgetModule/helpers/createBudgetQueryKeys';

type CanResourceEditPayload = {
    data: {
        bpId: number;
        softwareIds: string[];
    };
};

type ResourceOptionsPayload = {
    optionsPerBp: BudgetProposalByResourcesOptions;
};

export const useGetResourceOptions = (): UseQueryResult<
    BudgetProposalByResourcesOptions,
    HttpError
> =>
    useQuery<BudgetProposalByResourcesOptions, HttpError>({
        queryKey: ['can_resource_edit'],

        queryFn: async () => {
            const response = await RQAPICallWithData<ResourceOptionsPayload>({
                endpoint: 'budget/admin/create_resource_options',
                method: 'GET',
            });
            return response.content.data.optionsPerBp;
        },
    });

const updateCanResourceEditCache = (
    oldBudgetProposals:
        | BudgetProposalT[]
        | BudgetProposalWithResourcesT[]
        | undefined,
    { data }: CanResourceEditPayload,
): BudgetProposalT[] | BudgetProposalWithResourcesT[] | undefined => {
    if (!oldBudgetProposals) return oldBudgetProposals;
    return oldBudgetProposals.map((oldBudgetProposal) => {
        if (oldBudgetProposal.id !== data.bpId) return oldBudgetProposal;
        return {
            ...oldBudgetProposal,
            canResourceEdit: data.softwareIds,
        };
    });
};

export function useMutateResourceCanEdit(budgetProposal: BudgetProposalT): {
    mutate: (softwareIds: (string | null)[]) => void;
} {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { budgetProposalsKeys } = createBudgetQueryKeys();
    const mutation = useMutation({
        mutationFn: (arg: (string | null)[]) =>
            RQAPICall<CanResourceEditPayload>({
                endpoint: `budget_proposals/${budgetProposal.id}/can_resource_edit`,
                method: 'POST',
                body: { softwareIds: arg },
            }),

        onSuccess: ({ content }) => {
            const updateCache = (key: BudgetProposalsQueryKey) =>
                queryClient.setQueryData(
                    key,
                    (
                        oldBudgetProposals:
                            | BudgetProposalWithResourcesT[]
                            | BudgetProposalT[],
                    ) =>
                        updateCanResourceEditCache(oldBudgetProposals, content),
                );
            updateCache(budgetProposalsKeys({ withResources: true }));
            updateCache(budgetProposalsKeys());
            dispatch.vision.notifyUser({
                message: t('adminBudgetSpace.resourceCanEditUpdated'),
                snackType: 'success',
            });
        },
    });
    return mutation;
}
